/**
 * Calendar Domain Adapter & Interaction Handler (Sprint AP-CAL-01)
 * Translates calendar UI drag-to-reschedule events into validated domain commands.
 */

import type {
  HerxCalendarItem,
  CalendarCommand,
  CalendarItemSource,
} from './calendar-types.js';

export interface CalendarFilterOptions {
  sources?: readonly CalendarItemSource[];
  includeCompleted?: boolean;
  searchQuery?: string;
  timeRange?: {
    startMs: number;
    endMs: number;
  };
}

export class CalendarAdapter {
  private items: HerxCalendarItem[] = [];

  constructor(initialItems: HerxCalendarItem[] = []) {
    this.items = [...initialItems];
  }

  setItems(items: HerxCalendarItem[]): void {
    this.items = [...items];
  }

  getItems(): readonly HerxCalendarItem[] {
    return this.items;
  }

  filterItems(options: CalendarFilterOptions): HerxCalendarItem[] {
    return this.items.filter((item) => {
      // 1. Source filter
      if (options.sources && options.sources.length > 0) {
        if (!options.sources.includes(item.source)) return false;
      }

      // 2. Completed filter
      if (options.includeCompleted === false && item.status === 'done') {
        return false;
      }

      // 3. Search query
      if (options.searchQuery && options.searchQuery.trim()) {
        const q = options.searchQuery.toLowerCase();
        const matchesTitle = item.title.toLowerCase().includes(q);
        const matchesDesc = item.description?.toLowerCase().includes(q);
        if (!matchesTitle && !matchesDesc) return false;
      }

      // 4. Time range filter
      if (options.timeRange) {
        const itemStart = item.startAt || item.dueAt || 0;
        const itemEnd = item.endAt || itemStart;
        if (itemEnd < options.timeRange.startMs || itemStart > options.timeRange.endMs) {
          return false;
        }
      }

      return true;
    });
  }

  createRescheduleCommand(
    eventId: string,
    newStartMs: number,
    newEndMs?: number
  ): { success: boolean; command?: CalendarCommand; error?: string } {
    const item = this.items.find((i) => i.eventId === eventId);
    if (!item) {
      return { success: false, error: 'Calendar item not found' };
    }

    if (item.readOnly) {
      return { success: false, error: 'Cannot reschedule a read-only item' };
    }

    const duration = (item.endAt && item.startAt) ? item.endAt - item.startAt : 3600000;
    const computedEnd = newEndMs ?? (newStartMs + duration);

    if (item.source === 'action-planner') {
      const command: CalendarCommand = {
        type: 'RESCHEDULE_TASK',
        taskId: item.sourceId,
        scheduledStartAt: newStartMs,
        scheduledEndAt: computedEnd,
        dueAt: computedEnd,
      };
      return { success: true, command };
    }

    if (item.source === 'appointment') {
      const command: CalendarCommand = {
        type: 'RESCHEDULE_APPOINTMENT',
        appointmentId: item.sourceId,
        startAt: newStartMs,
        endAt: computedEnd,
        timeZone: item.timeZone || 'UTC',
      };
      return { success: true, command };
    }

    if (item.source === 'crm-followup') {
      const command: CalendarCommand = {
        type: 'UPDATE_FOLLOWUP_SCHEDULE',
        followUpId: item.sourceId,
        scheduledAt: newStartMs,
        dueAt: computedEnd,
      };
      return { success: true, command };
    }

    return { success: false, error: `Unsupported item source: ${item.source}` };
  }

  optimisticReschedule(
    eventId: string,
    newStartMs: number,
    newEndMs?: number
  ): { rollback: () => void } {
    const original = [...this.items];
    const index = this.items.findIndex((i) => i.eventId === eventId);
    if (index !== -1) {
      const item = this.items[index];
      const duration = (item.endAt && item.startAt) ? item.endAt - item.startAt : 3600000;
      this.items[index] = {
        ...item,
        startAt: newStartMs,
        endAt: newEndMs ?? (newStartMs + duration),
        dueAt: newEndMs ?? (newStartMs + duration),
      };
    }

    return {
      rollback: () => {
        this.items = original;
      },
    };
  }
}
